// biome-ignore-all lint/plugin/no-unlabeled-form-inputs: legacy unlabeled inputs, burn-down tracked in HTUI-392
import React, {
  type KeyboardEventHandler,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Box,
  Button,
  ButtonGroup,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Column,
  GroupedCombobox,
  IconBox,
  Portal,
  Row,
  SearchInput,
  SegmentedControl,
  SegmentedControlOption,
  Skeleton,
  SkeletonBox,
  SparkleIcon,
  Spinner,
  Text,
  popoverVariants,
} from "@hightouch/htui";
import * as analytics from "src/lib/analytics";
import { useFlags } from "launchdarkly-react-client-sdk";
import groupBy from "lodash/groupBy";
import isEqual from "lodash/isEqual";
import partition from "lodash/partition";
import sortBy from "lodash/sortBy";
import pluralize from "pluralize";
import { LinkButton } from "src/router";

import type { MinimalParentModel } from "src/components/audiences/types";
import { DataTooltip } from "src/components/explore/filter-popover/data-tooltip";
import { ExternalLink } from "src/components/links/external-link";
import { useArrowKeyFocus } from "src/hooks/use-arrow-key-focus";
import { useRelativeViewportSize } from "src/hooks/use-relative-viewport-size";
import { type Condition, ConditionType } from "src/types/visual";
import { useQueryBuilderContext } from "src/components/explore/context/hooks";

import {
  EventColumn,
  FilterOption,
  MAX_INPUT_WIDTH_PX,
  getMaxPopoverHeightPx,
  METADATA_SECTION_WIDTH_PX,
  MetadataIcons,
  MetadataType,
  PADDING,
  PLACEMENT_INFLECTION_POINT,
  RelationColumn,
} from "./constants";
import { FilterPopoverOption } from "./filter-popover-option";
import { FilterPopoverTab } from "./filter-popover-tab";
import { FilterSelectButton } from "./filter-select-button";
import { GroupLabels } from "./group-labels";
import {
  CREATE_JOURNEY_TRAIT_LABEL,
  useConditionSelect,
} from "./use-condition-select";
import { type OptionVirtualItem, useVirtualItems } from "./use-virtual-items";
import {
  getIconProps,
  getMetadata,
  getNextEnabledOptionItem,
  getSearchPlaceholderText,
  getSetupLink,
  isColumnSelected,
} from "./utils";
import type { FilterColumnOption, MessageEventFilterOption } from "./types";
import { FilterPopoverOptionMetadata } from "./filter-popover-option-metadata";
import { useFilterableOptionsContext } from "./filterable-options-hook";
import { useAgentControlContext } from "src/components/llm/embedded-chat/agent-control-context";
import { getColumnFromValue } from "src/components/explore/visual/utils";

const TAB_SECTION_WIDTH_PX = [160, 160, 160, 192, 192];
const CONTENT_SECTION_WIDTH_PX = [208, 224, 256, 288, 320];
export const AI_SUGGESTIONS_GROUP_LABEL =
  "Suggested fields based on your search";

export interface FilterPopoverProps<TCondition extends Condition> {
  condition?: TCondition;
  hasError?: boolean;
  renderTriggerButton?: (props: { isLoading: boolean }) => ReactNode;
  isLoading?: boolean;
  isDisabled?: boolean;
  parent: MinimalParentModel | undefined | null;
  onChange: (newCondition: Condition) => void;
  // Temporary, in new designs this won't be needed
  placeholder?: string;
}

function EmptyFilterState({
  title,
  description,
  action,
}: {
  title: string;
  description: ReactNode;
  action: ReactNode;
}) {
  return (
    <>
      <Column mt={4}>
        <Text fontWeight="semibold" color="text.secondary">
          {title}
        </Text>
        <Text mt={1} mx={8} color="text.tertiary">
          {description}
        </Text>
      </Column>
      <ButtonGroup mx="auto" mt={6}>
        {action}
      </ButtonGroup>
    </>
  );
}

export function FilterPopover<TCondition extends Condition>({
  condition,
  isDisabled = false,
  isLoading: isLoadingProp = false,
  hasError = false,
  renderTriggerButton,
  parent,
  onChange,
  placeholder,
}: FilterPopoverProps<TCondition>): JSX.Element {
  const {
    buildCondition,
    sections: tabs,
    modelName,
  } = useFilterableOptionsContext();
  const { onCreateJourneyTrait } = useQueryBuilderContext();
  const { showMergedModelLabel } = useFlags();

  const searchRef = useRef<HTMLInputElement>(null);
  const optionsContainer = useRef<HTMLDivElement>(null);

  const { relativeHeight } = useRelativeViewportSize();

  const [focusedTabIndex, setFocusedTabIndex] = useArrowKeyFocus(tabs.length);

  const {
    columns,
    filter,
    search,
    selectedColumn,
    filterableColumnsLoading,
    aiSuggestionsLoading,
    aiSuggestionsEnabled,
    onFilterChange,
    onReset,
    onResetSelectedTab,
    onSearch,
  } = useConditionSelect({
    condition,
    parentModelId: parent?.id,
  });

  const isLoading = isLoadingProp || filterableColumnsLoading;

  const [hoveredOption, setHoveredOption] = useState<FilterColumnOption | null>(
    null,
  );

  const [selectedGroupIndex, setSelectedGroupIndex] = useState(0);

  // When a tab has multiple option groups, switch the SegmentedControl to
  // the group containing the currently selected option so it's visible
  // without making the user hunt for it.
  useEffect(() => {
    if (!condition) return;
    const groupIdx = columns.findIndex((g) =>
      g.options.some((opt) => isColumnSelected(condition, opt)),
    );
    if (groupIdx >= 0) setSelectedGroupIndex(groupIdx);
  }, [condition, columns]);

  const visibleOptions = useMemo(
    () => columns[selectedGroupIndex]?.options ?? [],
    [columns, selectedGroupIndex],
  );

  const optionGroups = useMemo(() => {
    if (visibleOptions.length === 0 && !aiSuggestionsLoading) {
      return [];
    }

    // Split AI-suggested items from regular results
    const [aiSuggestedColumns, regularColumns] = partition(
      visibleOptions,
      (column) => column.isAISuggested,
    );

    const aiGroup =
      aiSuggestionsLoading || aiSuggestedColumns.length > 0
        ? [
            {
              label: AI_SUGGESTIONS_GROUP_LABEL,
              options: aiSuggestedColumns,
              skeletonCount: aiSuggestionsLoading ? 3 : 0,
            },
          ]
        : [];

    const keepGroup = (group: {
      options: FilterColumnOption[];
      skeletonCount?: number;
    }) => group.options.length > 0 || (group.skeletonCount ?? 0) > 0;

    if (filter === FilterOption.MessageEvent) {
      const messageEventColumns = regularColumns.filter(
        (col): col is MessageEventFilterOption =>
          col.type === FilterOption.MessageEvent,
      );
      return Object.entries(groupBy(messageEventColumns, "group"))
        .map(([label, options]) => ({ label, options }))
        .filter(keepGroup);
    }

    if (filter !== FilterOption.Property) {
      return [
        {
          label: aiSuggestionsEnabled ? "Results" : null,
          options: regularColumns,
        },
        ...aiGroup,
      ].filter(keepGroup);
    }

    // Property filters include merged columns.
    // If merged columns are present, we want to group by the model name.
    const [parentModelColumns, mergedColumns] = partition(
      regularColumns,
      (column) => column.value.type === "raw",
    );
    const mergedColumnsByModelName = groupBy(mergedColumns, "modelName");

    const showModelName = mergedColumns.length > 0 || aiGroup.length > 0;
    return [
      {
        label: showModelName
          ? (modelName ?? parent?.name ?? "Parent model")
          : null,
        options: parentModelColumns,
      },
      ...sortBy(
        Object.entries(mergedColumnsByModelName).map(
          ([modelName, columns]) => ({
            label: modelName,
            options: columns,
          }),
        ),
        (group) => group.label.toLowerCase(),
      ),
      ...aiGroup,
    ].filter(keepGroup);
  }, [
    visibleOptions,
    filter,
    modelName,
    parent?.name,
    aiSuggestionsEnabled,
    aiSuggestionsLoading,
  ]);

  const { virtualizer, virtualItems } = useVirtualItems({
    optionGroups,
    containerRef: optionsContainer,
  });

  const changeFocus: (index: number) => KeyboardEventHandler =
    (index) => (event) => {
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setFocusedTabIndex(index - 1);
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        setFocusedTabIndex(index + 1);
      }
    };

  const applyColumn = (column: FilterColumnOption) => {
    if (
      column.type === FilterOption.InlineTrait &&
      column.label === CREATE_JOURNEY_TRAIT_LABEL &&
      onCreateJourneyTrait
    ) {
      onCreateJourneyTrait();
      return;
    }
    onChange(buildCondition(column));
  };

  const selectOption = (column: FilterColumnOption, onClose: () => void) => {
    onClose();
    if (column.isAISuggested) {
      analytics.track("Audiences Agent Filter Suggestion Clicked");
    }
    applyColumn(column);
  };

  // Focus stays in the search input, so arrow keys drive the `hoveredOption`
  // highlight through the visible options and Enter selects the highlighted one.
  const handleSearchKeyDown =
    (onClose: () => void): KeyboardEventHandler<HTMLInputElement> =>
    (event) => {
      const optionItems = virtualItems.filter(
        (item): item is OptionVirtualItem<FilterColumnOption> =>
          item.type === "option",
      );
      if (optionItems.length === 0) {
        return;
      }

      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        const currentIndex = optionItems.findIndex((item) =>
          isEqual(item.option, hoveredOption),
        );
        const next = getNextEnabledOptionItem(
          optionItems,
          currentIndex,
          event.key === "ArrowDown" ? "down" : "up",
        );
        if (!next) {
          return;
        }
        setHoveredOption(next.option);
        virtualizer.scrollToIndex(virtualItems.indexOf(next));
      } else if (event.key === "Enter" && hoveredOption) {
        // The hovered option can go stale when the visible list changes
        // underneath it (e.g. the group switches or columns reload), so only
        // select it if it's still among the currently visible options.
        const hoveredItem = optionItems.find((item) =>
          isEqual(item.option, hoveredOption),
        );
        if (hoveredItem && !hoveredItem.option.isDisabled) {
          event.preventDefault();
          selectOption(hoveredItem.option, onClose);
        }
      }
    };

  const placeholderText = getSearchPlaceholderText(filter);
  const missingColumnTypeText =
    filter === FilterOption.All ? "column" : filter.toLowerCase();
  let buttonContent: ReactNode = selectedColumn?.label;

  const selectedColumnMetadata = getMetadata(selectedColumn);

  const showColumnMetadataLabelOnButton =
    selectedColumnMetadata?.type === MetadataType.MergedColumn
      ? showMergedModelLabel
      : true;

  if (selectedColumn?.label && selectedColumnMetadata) {
    buttonContent = (
      <Row maxWidth={`${MAX_INPUT_WIDTH_PX}px`} gap={2}>
        <Text isTruncated>{selectedColumn.label}</Text>
        <Row
          as={Text}
          align="center"
          color="text.secondary"
          gap={1}
          overflow="hidden"
          sx={{ svg: { height: "12px", width: "12px", flexShrink: 0 } }}
        >
          {MetadataIcons[selectedColumnMetadata.type]}
          {showColumnMetadataLabelOnButton && (
            <Text isTruncated color="text.secondary">
              {selectedColumnMetadata.modelName}
            </Text>
          )}
        </Row>
      </Row>
    );
  }

  const propertyTab = useMemo(
    () => tabs.find((t) => t.type === FilterOption.Property),
    [tabs],
  );

  // Use the section tab's icon for the trigger button when overrideColumnIcon is set,
  // keeping it consistent with how list items resolve their icons.
  const iconOverride = useMemo(() => {
    if (!selectedColumn) return undefined;
    if ("iconOverride" in selectedColumn && selectedColumn.iconOverride) {
      return selectedColumn.iconOverride;
    }
    if (
      selectedColumn.type === FilterOption.Property &&
      propertyTab?.overrideColumnIcon
    ) {
      return {
        icon: propertyTab.icon,
        color: propertyTab.shade,
        variant: propertyTab.iconVariant,
      };
    }
    return undefined;
  }, [selectedColumn, propertyTab]);

  const iconProps = getIconProps(selectedColumn?.type, {
    invertColors: selectedColumn?.invertIconColors,
    iconOverride,
  });

  const { agentAvailable, nudgeAgent } = useAgentControlContext();
  const onAgentNudgeClick = useCallback(
    (searchText: string) => {
      if (!nudgeAgent) return;

      const prompt = searchText
        ? `Help me find a filter ${missingColumnTypeText} for "${searchText}"`
        : `Help me find the right filter ${missingColumnTypeText}`;
      nudgeAgent(prompt);
    },
    [nudgeAgent, missingColumnTypeText],
  );

  if (tabs.length === 1 && !renderTriggerButton) {
    return (
      <GroupedCombobox
        width="2xs"
        isLoading={isLoading}
        isDisabled={isDisabled || isLoading}
        isInvalid={hasError}
        optionDescription={(option) => option.description || ""}
        placeholder={placeholder || tabs[0]!.placeholder || "Select an option…"}
        optionGroups={optionGroups.map((o) => ({
          ...o,
          label: o.label ?? "Properties",
        }))}
        value={selectedColumn?.value ?? null}
        onChange={(value) => {
          const column = getColumnFromValue(
            optionGroups.flatMap(({ options }) => options),
            value,
          );
          if (column) {
            applyColumn(column);
          }
        }}
      />
    );
  }

  return (
    <Popover
      isLazy
      preventOverflow
      initialFocusRef={searchRef}
      placement={
        relativeHeight > PLACEMENT_INFLECTION_POINT ? "bottom-start" : "right"
      }
      returnFocusOnClose={false}
      onOpen={onResetSelectedTab}
      onClose={() => {
        onReset();
        setHoveredOption(null);
      }}
    >
      {({ onClose }) => (
        <>
          <Row
            gap={2}
            sx={{ "&>div": { alignSelf: "start" } }}
            key="filter-popover-trigger"
          >
            <PopoverTrigger>
              {renderTriggerButton ? (
                renderTriggerButton({
                  isLoading,
                })
              ) : (
                <FilterSelectButton
                  hasError={hasError}
                  isDisabled={isDisabled || isLoading}
                >
                  <DataTooltip
                    isDisabled={!selectedColumn || isLoading}
                    description={selectedColumn?.description}
                    label={selectedColumn?.label ?? ""}
                    placement="bottom-start"
                    subtitle={
                      selectedColumn && selectedColumnMetadata?.modelName ? (
                        <Row
                          as={Text}
                          align="center"
                          color="base.divider"
                          columnGap={1}
                          display="inline-block"
                          flexWrap="wrap"
                          size="sm"
                          textAlign="left"
                          wordBreak="break-all"
                          sx={{ svg: { height: "12px", width: "12px" } }}
                        >
                          {[
                            FilterOption.Trait,
                            FilterOption.TraitTemplate,
                          ].includes(selectedColumn.type)
                            ? "Based on"
                            : "Merged from"}{" "}
                          {selectedColumnMetadata.type === MetadataType.Event
                            ? EventColumn.icon
                            : RelationColumn.icon}{" "}
                          {selectedColumnMetadata.modelName}
                        </Row>
                      ) : null
                    }
                    title={selectedColumn?.type ?? FilterOption.All}
                  >
                    {isLoading ? (
                      <Spinner size="xs" />
                    ) : (
                      <Row gap={2} pl={1.5} pr={2} py={1.5}>
                        {iconProps?.icon && (
                          <IconBox size="sm" {...iconProps} />
                        )}
                        {selectedColumn ? (
                          <Row flex="0 1 fit-content" maxWidth="300px">
                            <Text isTruncated>{buttonContent}</Text>
                          </Row>
                        ) : (
                          "Select a property…"
                        )}
                      </Row>
                    )}
                  </DataTooltip>
                </FilterSelectButton>
              )}
            </PopoverTrigger>

            {condition && condition.type === ConditionType.SegmentSet && (
              <ExternalLink
                tooltipMessage="Open Audience"
                url={`/audiences/${condition.modelId}`}
              />
            )}
          </Row>

          <Portal key="filter-popover-portal">
            <PopoverContent
              boxShadow="lg"
              borderRadius="2xl"
              width="auto"
              motionProps={popoverVariants}
            >
              <PopoverBody p={0} flex="1 0 auto">
                <Row
                  minHeight={0}
                  flex={1}
                  maxHeight={`${getMaxPopoverHeightPx(tabs.length)}px`}
                >
                  <Column
                    flex="0 0 auto"
                    role="tablist"
                    borderRightWidth="hairline"
                    borderRightStyle="solid"
                    borderColor="base.divider"
                    overflowY="auto"
                    overflowX="visible"
                    py={PADDING}
                    gap={2}
                    width={TAB_SECTION_WIDTH_PX}
                  >
                    {tabs.map((tab, index) => {
                      return (
                        <React.Fragment key={tab.label}>
                          {tab.heading && (
                            <Text ml={2} size="sm">
                              {tab.heading}
                            </Text>
                          )}
                          <FilterPopoverTab
                            label={tab.label}
                            tip={tab.tip}
                            isFocused={focusedTabIndex === index}
                            isSelected={filter === tab.type}
                            iconProps={{
                              icon: tab.icon,
                              color: tab.shade,
                              variant: tab.iconVariant,
                            }}
                            onKeyDown={changeFocus(index)}
                            onClick={() => {
                              onFilterChange(tab.type);
                              setSelectedGroupIndex(0);
                              setHoveredOption(null);
                              searchRef.current?.focus();
                            }}
                            separator={tab.separator}
                          />
                        </React.Fragment>
                      );
                    })}
                  </Column>

                  <Column
                    role="tabpanel"
                    position="relative"
                    flex="0 0 auto"
                    width={CONTENT_SECTION_WIDTH_PX}
                    borderRightWidth="hairline"
                    borderRightStyle="solid"
                    borderColor="base.divider"
                  >
                    <Column
                      py={3}
                      px={2}
                      borderBottomWidth="hairline"
                      borderBottomStyle="solid"
                      borderColor="base.divider"
                    >
                      <SearchInput
                        ref={searchRef}
                        placeholder={placeholderText}
                        width="100%"
                        value={search}
                        onChange={(event) => {
                          // The visible options change as the query changes, so
                          // reset the highlight to start arrow nav from the top.
                          setHoveredOption(null);
                          onSearch(event);
                        }}
                        onKeyDown={handleSearchKeyDown(onClose)}
                      />
                    </Column>
                    {columns.length > 1 && (
                      <Box mx={2} mt={2}>
                        <SegmentedControl
                          width="100%"
                          value={columns[selectedGroupIndex]?.label ?? ""}
                          onChange={(label) => {
                            const idx = columns.findIndex(
                              (g) => g.label === label,
                            );
                            if (idx >= 0) {
                              setSelectedGroupIndex(idx);
                              setHoveredOption(null);
                            }
                          }}
                        >
                          {columns.map((group) => (
                            <SegmentedControlOption
                              key={group.label}
                              label={group.label ?? ""}
                              value={group.label ?? ""}
                            />
                          ))}
                        </SegmentedControl>
                      </Box>
                    )}
                    {agentAvailable &&
                      visibleOptions.length > 0 &&
                      !(aiSuggestionsEnabled && search.trim().length > 0) && (
                        <Row
                          as="button"
                          align="center"
                          gap={2}
                          px={3}
                          py={2}
                          mx={2}
                          mt={2}
                          borderRadius="md"
                          cursor="pointer"
                          color="text.secondary"
                          _hover={{ bg: "base.lightBackground" }}
                          onClick={() => {
                            onClose();
                            onAgentNudgeClick(search);
                          }}
                        >
                          <Box
                            as={SparkleIcon}
                            fontSize="16px"
                            flexShrink={0}
                          />
                          <Text fontWeight="medium" size="sm">
                            Ask an agent
                          </Text>
                        </Row>
                      )}

                    <Column
                      ref={optionsContainer}
                      position="relative"
                      // Keeps the sticky group labels' zIndex inside this list, so their
                      // white background can't paint over the SegmentedControl thumb above.
                      isolation="isolate"
                      height={`${getMaxPopoverHeightPx(tabs.length)}px`}
                      p={2}
                      overflowY="auto"
                      width={CONTENT_SECTION_WIDTH_PX}
                    >
                      {visibleOptions.length === 0 && !aiSuggestionsLoading && (
                        <Column
                          flex={1}
                          minHeight={0}
                          justify="center"
                          textAlign="center"
                        >
                          {agentAvailable ? (
                            <EmptyFilterState
                              title={`No ${pluralize(missingColumnTypeText)} found`}
                              description="Please check your search or use the agent to help you find the right field."
                              action={
                                <Button
                                  icon={SparkleIcon}
                                  onClick={() => {
                                    onClose();
                                    onAgentNudgeClick(search);
                                  }}
                                >
                                  Try with an agent
                                </Button>
                              }
                            />
                          ) : (
                            <EmptyFilterState
                              title={`No ${pluralize(missingColumnTypeText)} found`}
                              description={
                                <>
                                  {search.length > 0
                                    ? ` Please check your search or create a new ${missingColumnTypeText}.`
                                    : null}{" "}
                                  Would you like to create a new{" "}
                                  {missingColumnTypeText}?
                                </>
                              }
                              action={
                                <LinkButton
                                  href={getSetupLink(filter, parent?.id)}
                                  variant="secondary"
                                >
                                  Go to setup
                                </LinkButton>
                              }
                            />
                          )}
                        </Column>
                      )}

                      {/* Group labels are rendered separately.
                      This is to make sure the group labels can be sticky,
                      since the virtual items are removed from the DOM */}
                      <GroupLabels items={virtualItems} />

                      <Box
                        aria-label="Filter options"
                        role="listbox"
                        height={`${virtualizer.getTotalSize()}px`}
                        flexShrink={0}
                        position="relative"
                      >
                        {virtualizer.getVirtualItems().map((virtualItem) => {
                          const item = virtualItems[virtualItem.index];

                          if (!item) {
                            return null;
                          }

                          if (item.type === "group") {
                            return (
                              <Row
                                id="placeholder"
                                key={virtualItem.key}
                                align="center"
                                width="100%"
                                sx={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  height: `${virtualItem.size}px`,
                                  transform: `translateY(${virtualItem.start}px)`,
                                }}
                              />
                            );
                          }

                          if (item.type === "skeleton") {
                            return (
                              <Skeleton key={virtualItem.key} isLoading>
                                <Row
                                  align="center"
                                  gap={2}
                                  px={2}
                                  sx={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    height: `${virtualItem.size}px`,
                                    transform: `translateY(${virtualItem.start}px)`,
                                    width: "100%",
                                  }}
                                >
                                  <SkeletonBox
                                    w="20px"
                                    h="20px"
                                    borderRadius="sm"
                                  />
                                  <SkeletonBox
                                    w="60%"
                                    h="12px"
                                    borderRadius="sm"
                                  />
                                </Row>
                              </Skeleton>
                            );
                          }

                          const column = item?.option;
                          // TODO(nolan): this logic should also be brought into the constants config
                          // and we shouldn't need "override" prop
                          const tabConfig = tabs.find(
                            (t) => t.type === column.type,
                          );
                          const iconOverride =
                            "iconOverride" in column && column.iconOverride
                              ? column.iconOverride
                              : tabConfig && tabConfig.overrideColumnIcon
                                ? {
                                    icon: tabConfig.icon,
                                    color: tabConfig.shade,
                                    variant: tabConfig.iconVariant,
                                  }
                                : undefined;
                          const iconProps = getIconProps(column?.type, {
                            invertColors: column?.invertIconColors,
                            iconOverride,
                          });

                          if (!iconProps) {
                            return null;
                          }

                          return (
                            <FilterPopoverOption
                              key={virtualItem.key}
                              iconProps={iconProps}
                              optionType={column.type}
                              columnType={
                                (column.type === FilterOption.Property &&
                                  column.propertyType) ||
                                undefined
                              }
                              semanticType={
                                "semanticType" in column
                                  ? column.semanticType
                                  : null
                              }
                              // Keep the focus state in sync with the hovered state
                              // until the user goes to another an option or closes the popover
                              isFocused={isEqual(column, hoveredOption)}
                              isSelected={
                                Boolean(condition) &&
                                isColumnSelected(condition, column)
                              }
                              isDisabled={column.isDisabled}
                              isHightouchManaged={column.isHightouchManaged}
                              badge={column.badge}
                              label={column.label}
                              description={column?.description}
                              metadata={getMetadata(
                                // Don't show merged names in 'properties' view
                                filter === FilterOption.Property
                                  ? undefined
                                  : column,
                              )}
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                height: `${virtualItem.size}px`,
                                transform: `translateY(${virtualItem.start}px)`,
                              }}
                              onClick={() => selectOption(column, onClose)}
                              onFocus={() => setHoveredOption(column)}
                              onPointerEnter={() => setHoveredOption(column)}
                            />
                          );
                        })}
                      </Box>
                    </Column>
                  </Column>

                  <Column
                    role="tabpanel"
                    position="relative"
                    flex="0 0 auto"
                    overflow="auto"
                    p={4}
                    width={METADATA_SECTION_WIDTH_PX}
                  >
                    <FilterPopoverOptionMetadata
                      option={hoveredOption || selectedColumn}
                      sourceId={parent?.connection?.id}
                    />
                  </Column>
                </Row>
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </>
      )}
    </Popover>
  );
}
